import { useState } from "react";
import axios from "axios";
import { Edit3, Save, X, Loader2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { PinDropMap } from "@/components/PinDropMap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin/mod tower edit dialog. Mirrors AdminEditDepotDialog. Uses PATCH so
 * only the fields you change are sent — no moderation queue.
 */
export function AdminEditTowerDialog({ tower, onChange }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const openDialog = () => {
    setForm({
      name: tower.name || "",
      call_letters: tower.call_letters || "",
      city: tower.city || "",
      state: tower.state || "",
      state_code: tower.state_code || "",
      latitude: tower.latitude ?? "",
      longitude: tower.longitude ?? "",
      status: tower.status || "preserved",
      year_built: tower.year_built ?? "",
      year_closed: tower.year_closed ?? "",
      railroad: tower.railroad || "",
      tower_type: tower.tower_type || "",
      description: tower.description || "",
      wikipedia_url: tower.wikipedia_url || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const lat = form.latitude === "" ? null : parseFloat(form.latitude);
    const lng = form.longitude === "" ? null : parseFloat(form.longitude);
    if (lat != null && (isNaN(lat) || lat < -90 || lat > 90)) return toast.error("Bad latitude");
    if (lng != null && (isNaN(lng) || lng < -180 || lng > 180)) return toast.error("Bad longitude");
    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        call_letters: form.call_letters.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        state_code: (form.state_code || "").trim().toUpperCase() || null,
        latitude: lat,
        longitude: lng,
        status: form.status,
        year_built: form.year_built === "" ? null : parseInt(form.year_built, 10),
        year_closed: form.year_closed === "" ? null : parseInt(form.year_closed, 10),
        railroad: form.railroad.trim() || null,
        tower_type: form.tower_type || null,
        description: form.description.trim() || null,
        wikipedia_url: form.wikipedia_url.trim() || null,
      };
      const r = await axios.patch(`${API}/admin/towers/${tower.id}`, payload, { withCredentials: true });
      toast.success("Tower updated");
      setOpen(false);
      onChange?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        className="w-full bg-amber-500/10 border-amber-500/40 text-amber-300 hover:bg-amber-500/20 hover:!text-amber-200 hover:border-amber-500/60"
        onClick={openDialog}
        data-testid="admin-edit-tower-btn"
      >
        <Edit3 className="w-4 h-4 mr-2" />
        Edit Tower (Admin)
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="sm:max-w-2xl bg-[#1a1a1a] border-white/10 max-h-[90vh] overflow-y-auto"
          data-testid="admin-edit-tower-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2 font-serif">
              <Edit3 className="w-4 h-4 text-amber-500" />
              Edit tower
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Changes save immediately — no moderation queue.
            </DialogDescription>
            {tower?.updated_at && (
              <p className="text-[11px] text-white/50 font-mono pt-1">
                Last edited by <span className="text-amber-400">{tower.updated_by || "unknown"}</span> ·{" "}
                {new Date(tower.updated_at).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </p>
            )}
          </DialogHeader>

          {form && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
              <div>
                <Label className="text-xs text-white/80">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" data-testid="tower-edit-name" />
              </div>
              <div>
                <Label className="text-xs text-white/80">Call letters</Label>
                <Input value={form.call_letters} onChange={(e) => setForm({ ...form, call_letters: e.target.value.toUpperCase() })} placeholder="JD, ALTO, WR" className="mt-1.5 bg-white/5 border-white/10 text-white font-mono uppercase" />
              </div>
              <div>
                <Label className="text-xs text-white/80">City</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-white/80">State</Label>
                  <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" />
                </div>
                <div>
                  <Label className="text-xs text-white/80">Code</Label>
                  <Input value={form.state_code} maxLength={2} onChange={(e) => setForm({ ...form, state_code: e.target.value.toUpperCase() })} className="mt-1.5 bg-white/5 border-white/10 text-white uppercase" />
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-white/80 mb-1.5 block">Drop a pin to set location</Label>
                <PinDropMap latitude={form.latitude} longitude={form.longitude} onPick={(lat, lng) => setForm({ ...form, latitude: lat.toFixed(6), longitude: lng.toFixed(6) })} height={220} />
              </div>
              <div>
                <Label className="text-xs text-white/80 flex items-center gap-1.5"><MapPin className="w-3 h-3 text-amber-500" />Latitude</Label>
                <Input type="number" step="0.000001" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white font-mono" />
              </div>
              <div>
                <Label className="text-xs text-white/80 flex items-center gap-1.5"><MapPin className="w-3 h-3 text-amber-500" />Longitude</Label>
                <Input type="number" step="0.000001" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white font-mono" />
              </div>
              <div>
                <Label className="text-xs text-white/80">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1.5 bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="preserved">Preserved</SelectItem>
                    <SelectItem value="abandoned">Abandoned</SelectItem>
                    <SelectItem value="demolished">Demolished</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-white/80">Tower type</Label>
                <Select value={form.tower_type || "none"} onValueChange={(v) => setForm({ ...form, tower_type: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1.5 bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                    <SelectItem value="none">—</SelectItem>
                    <SelectItem value="mechanical">Mechanical</SelectItem>
                    <SelectItem value="electro-mechanical">Electro-mechanical</SelectItem>
                    <SelectItem value="relay">Relay</SelectItem>
                    <SelectItem value="electronic">Electronic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-white/80">Year built</Label>
                <Input type="number" value={form.year_built} onChange={(e) => setForm({ ...form, year_built: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white font-mono" />
              </div>
              <div>
                <Label className="text-xs text-white/80">Year closed</Label>
                <Input type="number" value={form.year_closed} onChange={(e) => setForm({ ...form, year_closed: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white font-mono" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-white/80">Railroad</Label>
                <Input value={form.railroad} onChange={(e) => setForm({ ...form, railroad: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-white/80">Wikipedia URL</Label>
                <Input value={form.wikipedia_url} onChange={(e) => setForm({ ...form, wikipedia_url: e.target.value })} placeholder="https://en.wikipedia.org/wiki/..." className="mt-1.5 bg-white/5 border-white/10 text-white" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-white/80">Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1.5 bg-white/5 border-white/10 text-white resize-none" />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/80 hover:bg-white/5 hover:!text-white">
              <X className="w-4 h-4 mr-2" />Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="tower-save-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-2" />Save</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
